import React, { Component } from "react";
import styled from "styled-components";
import { Mic as MicIcon } from "@material-ui/icons";

import { delay } from "js/core/utilities/promiseHelper";
import { themeColors } from "js/react/sharedStyles";
import { FlexBox } from "js/react/components/LayoutGrid";
import { FlexSpacer, Gap10, Gap5 } from "js/react/components/Gap";
import { BlueButton } from "js/react/components/UiComponents";
import { Button } from "js/Components/Button";
import { TextField } from "js/Components/TextField";
import { AiImageStyles, ImageStyles } from "common/aiConstants";

import ExamplePrompts from "./ExamplePrompts";
import { InputContainer } from "./PaneComponents";
import { app } from "js/namespaces";

const Container = styled.div`
    width: 100%;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;

    label {
        font-size: 20px;
        font-weight: 600;
    }
`;

const SpeechToTextButton = styled(MicIcon) <{ selected: boolean }>`
    cursor: pointer;
    color: ${themeColors.ui_blue};
    animation: ${props => props.selected ? "element-pulse-opacity 1.5s infinite" : "none"};
    padding: 5px;
    border-radius: 50%;

    :active {
        background-color: #f9f9f9;
    }
`;

interface PromptInputProps {
    className?: string;
    label?: string;
    prompt: string;
    onPromptChange: (prompt: string) => void;
    onPromptBlur?: (prompt: string) => void;
    onPromptFinishTyping?: (prompt: string) => void;
    promptExamples?: string[];
    promptPlaceholder?: string;
    onEnterKeyPress?: (prompt: string) => void;
    actionButtonText?: string;
    onActionButtonClick?: () => void;
    previewButtonText?: string;
    onPreviewButtonClick?: () => void;
    previewButtonDisabled?: boolean;
    inputContainerHeight?: number;
    inputDisabled?: boolean;
    hideSpeechToText?: boolean;
    hideExamplePrompts?: boolean;
    imagePromptExamples?: ImageStyles[];
    onImageStyleChange?: (imageStyle: ImageStyles) => void;
    multiline?: boolean;
    theme?: any;
    inputRef?: React.RefObject<HTMLInputElement>;
    hideAdditionalContext?: boolean;
    labelGap?: React.ReactNode;
}

export const StyledPromptLabel = styled.label``;

interface PromptInputState {
    prompt: string;
    isUsingSpeechToText: boolean;
    isTypingPrompt: boolean;
}

class PromptInput extends Component<PromptInputProps, PromptInputState> {
    speechToText?: any;

    constructor(props) {
        super(props);

        this.state = {
            prompt: "",
            isUsingSpeechToText: false,
            isTypingPrompt: false,
        };
    }

    componentWillUnmount() {
        if (this.speechToText) {
            this.speechToText.stop();
        }
    }

    typePrompt = async (prompt: string) => {
        this.setState({ isTypingPrompt: true, prompt: "" });

        for (const char of prompt) {
            this.setState(({ prompt, ...state }) => ({ prompt: `${prompt}${char}`, ...state }));
            await delay(6);
        }

        this.props.onPromptChange(prompt);
        if (this.props.onPromptFinishTyping) {
            this.props.onPromptFinishTyping(prompt);
        }

        this.setState({ isTypingPrompt: false });
    }

    handleToggleSpeechToText = () => {
        const { isUsingSpeechToText } = this.state;

        if (isUsingSpeechToText) {
            this.setState({ isUsingSpeechToText: false });
            if (this.speechToText) {
                this.speechToText.stop();
                this.speechToText = undefined;
            }
            return;
        }

        // @ts-ignore
        this.speechToText = new (window.SpeechRecognition ?? window.webkitSpeechRecognition)();
        this.speechToText.interimResults = true;
        this.speechToText.continuous = true;

        this.speechToText.addEventListener("result", event => {
            const transcript = Array.from(event.results)
                .map(result => result[0])
                .map(result => result.transcript)
                .join("");

            if (transcript) {
                this.typePrompt(transcript);
            }
        });

        this.setState({ isUsingSpeechToText: true });

        this.speechToText.start();
    }

    handleChangeOnInput = (newPrompt: string) => {
        this.props.onPromptChange(newPrompt);
    }

    handleBlurOnInput = (newPrompt: string) => {
        this.props.onPromptChange(newPrompt);
        if (this.props.onPromptBlur) {
            this.props.onPromptBlur(newPrompt);
        }
    }

    handleEnterKeyOnInput = (newPrompt: string) => {
        this.props.onEnterKeyPress?.(newPrompt);
    }

    handleKeyDown = event => {
        if (event.key === "Enter") {
            // When multiline is enabled, allow Shift+Enter to create a new line.
            if (this.props.multiline && event.shiftKey) {
                return;
            }
            // For a regular Enter press, prevent the default action and submit.
            event.preventDefault();
            this.handleEnterKeyOnInput(this.props.prompt);
        }
    }

    handleExamplePromptClick = (examplePrompt: string) => {
        this.typePrompt(examplePrompt);
    }

    handleExampleImagePromptClick = (imageStyle: ImageStyles) => {
        const imageStylePrompt = imageStyle === "theme"
            ? (this.props.theme.imageStylePrompt || this.props.theme.get("imageStylePrompt"))
            : AiImageStyles[imageStyle].prompt;

        this.typePrompt(imageStylePrompt)
            .then(() => {
                const { onImageStyleChange } = this.props;
                onImageStyleChange && onImageStyleChange(imageStyle);
            });
    }

    render() {
        const {
            className = "",
            label,
            prompt,
            promptPlaceholder = "Enter your prompt here",
            promptExamples = [],
            imagePromptExamples = [],
            actionButtonText,
            onActionButtonClick,
            previewButtonText,
            onPreviewButtonClick,
            previewButtonDisabled,
            inputContainerHeight,
            inputDisabled = false,
            hideSpeechToText = false,
            hideExamplePrompts = false,
            multiline = false,
            inputRef,
            hideAdditionalContext = false,
            labelGap = <Gap10 />,
        } = this.props;
        const {
            prompt: statePrompt,
            isUsingSpeechToText,
            isTypingPrompt
        } = this.state;

        return (
            <Container className={className}>
                {label && <>
                    {/* @ts-ignore */}
                    <FlexBox left middle fillWidth className="prompt-input-label">
                        <label>{label}</label>
                    </FlexBox>
                    {labelGap}
                </>}
                <InputContainer className="prompt-input-container" height={inputContainerHeight}>
                    {!hideSpeechToText &&
                        <SpeechToTextButton
                            selected={isUsingSpeechToText}
                            onClick={this.handleToggleSpeechToText}
                        />
                    }
                    <TextField
                        value={isTypingPrompt ? statePrompt : prompt}
                        onChange={this.handleChangeOnInput}
                        onBlur={this.handleBlurOnInput}
                        onKeyDown={this.handleKeyDown}
                        placeholder={promptPlaceholder}
                        InputProps={{ disableUnderline: true }}
                        inputRef={inputRef}
                        disabled={isUsingSpeechToText || isTypingPrompt || inputDisabled}
                        multiline={multiline}
                        autoFocus
                    />
                </InputContainer>
                {!hideAdditionalContext && (
                    <>
                        <Gap5 />
                        {/* @ts-ignore */}
                        <FlexBox horizontalAlign="between" fillWidth>
                            {
                                !onPreviewButtonClick && <FlexSpacer />
                            }
                            {
                                !hideExamplePrompts && (
                                    <ExamplePrompts
                                        examplePrompts={promptExamples}
                                        onExamplePromptClick={this.handleExamplePromptClick}
                                        imageExamplePrompts={imagePromptExamples}
                                        onImageExamplePromptClick={this.handleExampleImagePromptClick}
                                    />
                                )
                            }
                            {
                                !!onPreviewButtonClick &&
                                <>
                                    <FlexSpacer />
                                    <Button unframed blue large
                                        disabled={isTypingPrompt || prompt.trim().length === 0 || previewButtonDisabled}
                                        onClick={onPreviewButtonClick}
                                    >
                                        {previewButtonText}
                                    </Button>
                                </>
                            }
                        </FlexBox>
                        <Gap10 />
                        {/* @ts-ignore */}
                        <FlexBox fillWidth>
                            <FlexSpacer />
                            {
                                !!onActionButtonClick &&
                                <BlueButton disabled={isTypingPrompt} onClick={onActionButtonClick}>{actionButtonText}</BlueButton>
                            }
                        </FlexBox>
                    </>
                )}
            </Container>);
    }
}

export default PromptInput;
